export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  ) {
    return (error as Record<string, unknown>).message as string;
  }

  return 'An unexpected error occurred';
};

export function formatInternationalPhoneNumber(raw: string): string {
  if (!raw) return '';

  // Allow only digits, +, and spaces
  const cleaned = raw
    .replace(/[^\d+\s]/g, '') // keep only +, digits, and spaces
    .replace(/(?!^)\+/g, ''); // remove any extra + beyond the first

  const digitsOnly = cleaned.replace(/[^\d]/g, '');

  // Case 1: Starts with +1 and has exactly 10 digits → US format
  if (/^\+1\d{10}$/.test(cleaned.replace(/\s/g, ''))) {
    const number = digitsOnly.slice(1);
    return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  }

  // Case 2: Exactly 10 digits → assume US format
  if (/^\d{10}$/.test(digitsOnly)) {
    return `+1 (${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  // Case 3: Starts with + but NOT +1 → return as-is, preserving first space if any
  if (cleaned.startsWith('+')) {
    return cleaned.replace(/\s+/g, ' '); // collapse multiple spaces
  }

  // Fallback: just return digits
  return digitsOnly;
}

export function parsePhoneNumberInput(raw: string): string {
  return raw.replace(/[^\d+\s]/g, '');
}

export const formatReportDate = (raw?: string | null): string => {
  if (!raw) return '';
  const date = new Date(raw);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

// Missing dates sort oldest so a desc sort keeps them at the bottom. Compares
// rather than subtracts, since -Infinity minus -Infinity is NaN.
const toTime = (raw?: string | null) => {
  if (!raw) return -Infinity;
  const time = new Date(raw).getTime();
  return isNaN(time) ? -Infinity : time;
};

export const compareReportDates = (a?: string | null, b?: string | null): number => {
  const timeA = toTime(a);
  const timeB = toTime(b);
  if (timeA === timeB) return 0;
  return timeA < timeB ? -1 : 1;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const parseEmailList = (
  rawInput: string
): { emails: string[]; invalid: string[] } => {
  const entries = rawInput
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return {
    emails: entries.filter((email) => emailRegex.test(email)),
    invalid: entries.filter((email) => !emailRegex.test(email))
  };
};
